import { useState, useEffect } from 'react';
import { Box, HStack, Menu, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Button, ClockIcon, Modal, Text } from '@pimm/base';
import { CleaningScheduleTypeEnum } from '@pimm/services/lib/sms-workforce/types';
import { TimePicker } from './time-picker';
import PopoverDateTime from './popover-date-time';

type PopoverBulkScheduleMenuProps = {
  isOpen: boolean;
  isBulk?: boolean;
  onOpen: () => void;
  onClose: () => void;
  scheduleType?: CleaningScheduleTypeEnum;
  /** DOP start hour sourced from dopBlocks[blockNumber=1].startTime. Defaults to 5. */
  dopStartHour?: number;
  isLoading?: boolean;
  scheduledDate?: Date;
  /** Weekly: weekStart; Monthly: monthStart */
  min?: Date;
  /** Weekly: weekEnd; Monthly: monthEnd */
  max?: Date;
  onApply: (dateTime: Date) => void;
  selectedCount?: number;
  trigger?: (triggerProps: any) => JSX.Element;
  /** Render content in a Modal instead of Menu — use when this component is nested inside a Modal */
  asModal?: boolean;
};

export const PopoverBulkScheduleMenu = ({
  isOpen,
  isBulk = false,
  onOpen,
  onClose,
  scheduleType = CleaningScheduleTypeEnum.Weekly,
  dopStartHour = 5,
  isLoading,
  scheduledDate,
  selectedCount = 0,
  min,
  max,
  onApply,
  trigger,
  asModal = false,
}: PopoverBulkScheduleMenuProps) => {
  const { t } = useTranslation();
  const isDaily = scheduleType === CleaningScheduleTypeEnum.Daily;

  const [dateTimeInput, setDateTimeInput] = useState<Date>(scheduledDate ?? new Date());
  const [dailyDayIdx, setDailyDayIdx] = useState(0);

  // Reset state when menu opens
  useEffect(() => {
    if (isOpen) {
      const base = scheduledDate ?? new Date();
      setDateTimeInput(base);

      // For daily: determine which chip to default to based on existing due date
      if (isDaily && scheduledDate) {
        const nextDate = new Date(scheduledDate);
        nextDate.setDate(nextDate.getDate() + 1);
        const hours = base.getHours();
        const isOvernight = hours < dopStartHour;
        setDailyDayIdx(isOvernight ? 1 : 0);
      } else {
        setDailyDayIdx(0);
      }
    }
  }, [isOpen]);

  // When daily day chip changes, update the date portion of dateTimeInput and clamp time
  const handleDailyDayIdxChange = (idx: number) => {
    setDailyDayIdx(idx);
    if (!scheduledDate) return;

    const baseDate = new Date(scheduledDate);
    baseDate.setDate(baseDate.getDate() + idx); // idx 0 = task day, idx 1 = next day

    const hours = dateTimeInput.getHours();
    const minutes = dateTimeInput.getMinutes();

    // Clamp time to valid range for the selected day
    let clampedH = hours;
    let clampedM = minutes;
    if (idx === 0) {
      // Task day: DOP_START_HOUR:00 → 23:55
      if (hours < dopStartHour) {
        clampedH = dopStartHour;
        clampedM = 0;
      }
    } else {
      // Next day: 0:00 → (DOP_START_HOUR - 1):55
      if (hours >= dopStartHour) {
        clampedH = 0;
        clampedM = 0;
      }
    }

    const newDate = moment(baseDate).set({ h: clampedH, m: clampedM, s: 0 }).toDate();
    setDateTimeInput(newDate);
  };

  // Compute TimePicker bounds for Daily mode so arrows are disabled at boundaries
  const minTime: Date | undefined = (() => {
    if (!isDaily) return undefined;
    if (dailyDayIdx === 0) {
      // Task day: earliest = dopStartHour:00
      const day = new Date(dateTimeInput);
      day.setHours(dopStartHour, 0, 0, 0);
      return day;
    }
    // Next day: earliest = 0:00
    const day = new Date(dateTimeInput);
    day.setHours(0, 0, 0, 0);
    return day;
  })();

  const maxTime: Date | undefined = (() => {
    if (!isDaily) return undefined;
    if (dailyDayIdx === 0) {
      // Task day: latest = 23:55
      const day = new Date(dateTimeInput);
      day.setHours(23, 55, 0, 0);
      return day;
    }
    // Next day: latest = (dopStartHour - 1):55
    const day = new Date(dateTimeInput);
    day.setHours(dopStartHour - 1, 55, 0, 0);
    return day;
  })();

  const scheduleContent = (
    <VStack space={3} p={4} w="full" rounded="lg" overflow="hidden">
      {/* Header */}
      <VStack space={1}>
        <Text size="lg" fontWeight={600} color="gray.900" lineHeight="xs">
          {t('cleaning_task:edit_schedule')}
        </Text>
        <Text size="md" fontWeight={500} color="gray.600" lineHeight="md">
          {isBulk ? t('cleaning_task:edit_schedule_bulk_selected_note', { selectedCount }) : t('cleaning_task:edit_schedule_note')}
        </Text>
      </VStack>

      {/* Schedule-type-aware date picker */}
      <PopoverDateTime
        scheduleType={scheduleType}
        value={dateTimeInput}
        taskDate={scheduledDate}
        dopStartHour={dopStartHour}
        dailyDayIdx={dailyDayIdx}
        min={min}
        max={max}
        onDateChange={setDateTimeInput}
        onDailyDayIdxChange={handleDailyDayIdxChange}
      />

      {/* Time picker */}
      <TimePicker
        value={dateTimeInput}
        onChange={setDateTimeInput}
        isPMDisabled={isDaily && dailyDayIdx === 1}
        minTime={minTime}
        maxTime={maxTime}
      />

      {/* Action buttons */}
      <HStack overflow="hidden" space={2} pt={2}>
        <Button
          width="1/3"
          variant="unstyled"
          size="lg"
          rounded="md"
          borderWidth={1}
          _text={{ fontSize: 'md', color: 'gray.700', fontWeight: 500 }}
          onPress={onClose}
        >
          {t('cleaning_task:cancel')}
        </Button>

        <Button
          flex={1}
          size="lg"
          rounded="md"
          _text={{ fontSize: 'md' }}
          onPress={() => onApply(dateTimeInput)}
          isLoading={isLoading}
          isLoadingText={t('cleaning_task:apply')}
        >
          {t('cleaning_task:apply')}
        </Button>
      </HStack>
    </VStack>
  );

  if (asModal) {
    return (
      <>
        {trigger ? (
          trigger({})
        ) : (
          <Button
            bg={selectedCount > 0 ? 'primary.400' : 'gray.200'}
            variant="unstyled"
            size="md"
            borderWidth={0}
            py={3}
            isDisabled={selectedCount === 0}
            onPress={() => selectedCount > 0 && onOpen()}
            startIcon={<ClockIcon size="14px" color="gray.800" />}
            _text={{ color: 'gray.800', fontWeight: 700, fontSize: 'lg' }}
          >
            {t('cleaning_task:set_time_apply')}
          </Button>
        )}
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          hideClose
          backdropOpacity={0}
          coverScreen={false}
          _content={{ rounded: 'xl', p: 0, w: 260, mb: 16 }}
        >
          <Box w="full" bg="white" shadow={6} rounded="xl" overflow="hidden">
            {scheduleContent}
          </Box>
        </Modal>
      </>
    );
  }

  return (
    <Menu
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom"
      p={0}
      mt={1}
      width={240}
      borderRadius="xl"
      overflow="hidden"
      trigger={triggerProps =>
        trigger ? (
          trigger(triggerProps)
        ) : (
          <Button
            {...triggerProps}
            bg={selectedCount > 0 ? 'primary.400' : 'gray.200'}
            variant="unstyled"
            size="md"
            borderWidth={0}
            py={3}
            isDisabled={selectedCount === 0}
            onPress={() => selectedCount > 0 && onOpen()}
            startIcon={<ClockIcon size="14px" color="gray.800" />}
            _text={{ color: 'gray.800', fontWeight: 700, fontSize: 'lg' }}
          >
            {t('cleaning_task:set_time_apply')}
          </Button>
        )
      }
    >
      <Box w="full" bg="white">
        {scheduleContent}
      </Box>
    </Menu>
  );
};

export default PopoverBulkScheduleMenu;
